import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { waLink } from "@/lib/modules";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { MessageCircle, Send, Settings2, Users, Plus, Trash2, Check, Search } from "lucide-react";
import { toast } from "sonner";

function firstName(n) { return (n || "").trim().split(/\s+/)[0] || ""; }
function fill(tpl, name) { return (tpl || "").replaceAll("{nome}", firstName(name)); }

function SettingsDialog({ settings, onSaved }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(settings.support_phone || "");
  const [templates, setTemplates] = useState(settings.support_templates || []);
  useEffect(() => { if (open) { setPhone(settings.support_phone || ""); setTemplates(settings.support_templates || []); } }, [open, settings]);

  const save = async () => {
    try {
      await api.put("/settings", { support_phone: phone, support_templates: templates.filter((t) => t.trim()) });
      toast.success("Configurações salvas");
      setOpen(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="support-settings-button"><Settings2 className="h-4 w-4" /> Configurar</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurar Atendimento</DialogTitle>
          <DialogDescription>Número central de suporte e modelos de mensagem.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Número central de suporte (WhatsApp)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Ex: 11999998888" className="mt-1.5" data-testid="support-phone-input" />
            <p className="text-xs text-muted-foreground mt-1">Aparece no botão flutuante de suporte para os usuários.</p>
          </div>
          <div>
            <Label>Modelos de mensagem (use {"{nome}"})</Label>
            <div className="space-y-2 mt-1.5">
              {templates.map((t, i) => (
                <div key={i} className="flex gap-2">
                  <Textarea value={t} onChange={(e) => setTemplates(templates.map((x, j) => j === i ? e.target.value : x))} rows={2} data-testid={`template-input-${i}`} />
                  <Button variant="ghost" size="icon" onClick={() => setTemplates(templates.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setTemplates([...templates, ""])} data-testid="add-template-button"><Plus className="h-4 w-4" /> Novo modelo</Button>
            </div>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="gap-2" data-testid="save-support-settings"><Check className="h-4 w-4" /> Salvar</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Atendimento() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin || user?.role === "coordenador";
  const [contacts, setContacts] = useState([]);
  const [settings, setSettings] = useState({ support_templates: [], support_phone: "" });
  const [msg, setMsg] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({});

  const loadSettings = useCallback(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setMsg((prev) => prev || (r.data.support_templates?.[0] || ""));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/support/contacts").then((r) => setContacts(r.data)).catch(() => {});
    loadSettings();
  }, [loadSettings]);

  const filtered = contacts.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q));
  const selectedList = filtered.filter((c) => selected[c.phone]);

  const openOne = (c) => {
    const link = waLink(c.phone, fill(msg, c.name));
    if (!link) return toast.error("Contato sem telefone válido");
    window.open(link, "_blank");
  };

  const openSelected = () => {
    if (selectedList.length === 0) return toast.error("Selecione ao menos um contato");
    selectedList.forEach((c, i) => {
      const link = waLink(c.phone, fill(msg, c.name));
      if (link) setTimeout(() => window.open(link, "_blank"), i * 400);
    });
    toast.success(`Abrindo ${selectedList.length} conversa(s) no WhatsApp… permita pop-ups se o navegador bloquear.`);
  };

  const allSelected = filtered.length > 0 && filtered.every((c) => selected[c.phone]);
  const toggleAll = () => {
    if (allSelected) setSelected({});
    else { const s = {}; filtered.forEach((c) => { s[c.phone] = true; }); setSelected(s); }
  };

  return (
    <div className="space-y-8 animate-fade-up pb-24" data-testid="atendimento-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
            <MessageCircle className="h-8 w-8 text-[hsl(var(--emerald))]" /> Atendimento
          </h1>
          <p className="text-muted-foreground mt-1">Fale com os membros pelo WhatsApp e atenda às necessidades.</p>
        </div>
        {isAdmin && <SettingsDialog settings={settings} onSaved={loadSettings} />}
      </div>

      <Card className="p-6 space-y-4">
        <div>
          <Label>Mensagem</Label>
          {settings.support_templates?.length > 0 && (
            <div className="mt-1.5 mb-2">
              <Select onValueChange={(v) => setMsg(v)}>
                <SelectTrigger className="w-full" data-testid="template-select"><SelectValue placeholder="Escolher um modelo…" /></SelectTrigger>
                <SelectContent>
                  {settings.support_templates.map((t, i) => <SelectItem key={i} value={t}>{t.slice(0, 60)}{t.length > 60 ? "…" : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="Digite a mensagem… use {nome} para personalizar" data-testid="support-message-input" />
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nome ou telefone" className="pl-9" data-testid="contact-search-input" />
          </div>
          <Button onClick={openSelected} className="gap-2" data-testid="broadcast-button">
            <Send className="h-4 w-4" /> Abrir selecionados ({selectedList.length})
          </Button>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-border bg-muted/40">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
          <span className="text-sm font-medium flex items-center gap-2"><Users className="h-4 w-4" /> {filtered.length} contato(s)</span>
        </div>
        {filtered.length === 0 ? (
          <p className="p-8 text-center text-muted-foreground text-sm">Nenhum contato com telefone encontrado.</p>
        ) : (
          <div className="divide-y divide-border">
            {filtered.map((c, idx) => (
              <div key={c.phone + c.name} className="flex items-center gap-3 px-5 py-3" data-testid="support-contact-row">
                <Checkbox checked={!!selected[c.phone]} onCheckedChange={(v) => setSelected({ ...selected, [c.phone]: !!v })} data-testid={`contact-check-${idx}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-xs text-muted-foreground">{c.phone} · {c.context}</div>
                </div>
                <Badge variant="secondary" className="text-[10px] hidden sm:inline-flex">{c.source === "membro" ? "Participante" : "Cadastro"}</Badge>
                <Button size="sm" variant="outline" className="gap-1.5 text-[hsl(var(--emerald))]" onClick={() => openOne(c)} data-testid={`whatsapp-contact-${idx}`}>
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
